/**
 * Partner session state: the email-code and Google sign-ins, org selection/creation,
 * whoami, and the role everything in the UI is gated on.
 *
 * States:
 *   loading   — restoring from a stored refresh token
 *   signed_out
 *   unscoped  — signed in, no org chosen yet (0 or 2+ memberships)
 *   ready     — scoped token + live role from `whoami`
 *
 * The role shown in the UI is always `whoami`'s (the LIVE membership role),
 * never the JWT claim, matching the server, which re-reads it per request.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiClient, createBrowserTokenStore, type TokenStore } from '../api/client';
import { ApiError } from '../api/errors';
import { partnerApi, type PartnerApi } from '../api/partner';
import type { MembershipSummary, Org, PartnerRole, PartnerType, TermsAcceptance, Whoami } from '../api/types';

export type SessionStatus = 'loading' | 'signed_out' | 'unscoped' | 'ready';

interface SessionValue {
  status: SessionStatus;
  api: PartnerApi;
  client: ApiClient;
  whoami: Whoami | null;
  org: Org | null;
  role: PartnerRole | null;
  memberships: MembershipSummary[];
  /** Why the last session ended, shown on the sign-in screen. */
  endedReason: string | null;
  requestCode: (email: string) => Promise<{ challengeId: string; expiresInS: number }>;
  verifyCode: (challengeId: string, code: string) => Promise<void>;
  /** "Continue with Google": trade a Google ID token for the same session verify issues. */
  signInWithGoogle: (credential: string) => Promise<void>;
  selectOrg: (partnerId: string) => Promise<void>;
  /** `terms` is the Partner Terms version the user ticked (required by the
   *  API). `afterScoped` runs with the new scoped token BEFORE the UI switches
   *  to the signed-in shell (used to create the first site). */
  /** Resolves to the new organisation's id. */
  createOrg: (name: string, type: PartnerType, terms: TermsAcceptance, afterScoped?: () => Promise<void>) => Promise<string>;
  refreshOrg: () => Promise<void>;
  /**
   * Re-read who is signed in after the account itself changed (the sign-in
   * email): rotates the tokens, whose claims carry the email, then whoami.
   */
  reloadIdentity: () => Promise<void>;
  /** Re-read whoami (after a claim, so the "confirm to take ownership" banner goes). */
  refreshWhoami: () => Promise<void>;
  /** The org has not accepted the current Partner Terms (whoami). */
  termsRequired: boolean;
  /** Clears `termsRequired` after `POST /partner/terms/accept` succeeded. */
  markTermsAccepted: () => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

const MEMBERSHIPS_KEY = 'fx-partner-memberships';

export function SessionProvider({
  children,
  store: injectedStore,
  fetchImpl,
  baseUrl,
}: {
  children: ReactNode;
  store?: TokenStore;
  fetchImpl?: typeof fetch;
  baseUrl?: string;
}) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [whoami, setWhoami] = useState<Whoami | null>(null);
  const [org, setOrg] = useState<Org | null>(null);
  const [memberships, setMemberships] = useState<MembershipSummary[]>(() => readMemberships());
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const endedRef = useRef<(e: ApiError) => void>(() => {});

  const store = useMemo(() => injectedStore ?? createBrowserTokenStore(), [injectedStore]);
  const client = useMemo(
    () => new ApiClient({ store, fetchImpl, baseUrl, onSessionEnded: (e) => endedRef.current(e) }),
    [store, fetchImpl, baseUrl],
  );
  const api = useMemo(() => partnerApi(client), [client]);

  endedRef.current = (error: ApiError) => {
    setEndedReason(error.message);
    setWhoami(null);
    setOrg(null);
    setStatus('signed_out');
  };

  /** After any token change: whoami decides scoped vs unscoped. */
  const hydrate = useCallback(async () => {
    const me = await api.whoami();
    setWhoami(me);
    if (me.partner_id && me.role) {
      setOrg(await api.getOrg());
      setStatus('ready');
    } else {
      setOrg(null);
      setStatus('unscoped');
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!store.getRefresh()) {
        setStatus('signed_out');
        return;
      }
      try {
        await client.refresh();
        if (!cancelled) await hydrate();
      } catch {
        if (!cancelled) setStatus('signed_out');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, store, hydrate]);

  const requestCode = useCallback(
    async (email: string) => {
      const res = await api.requestCode(email);
      return { challengeId: res.challenge_id, expiresInS: res.expires_in_s };
    },
    [api],
  );

  /** After a successful verify or Google sign-in: same response shape, same handling. */
  const acceptSignIn = useCallback(
    async (res: Awaited<ReturnType<PartnerApi['verifyCode']>>) => {
      store.set(res);
      setEndedReason(null);
      const list = res.memberships ?? [];
      setMemberships(list);
      writeMemberships(list);
      await hydrate();
    },
    [store, hydrate],
  );

  const verifyCode = useCallback(
    async (challengeId: string, code: string) => acceptSignIn(await api.verifyCode(challengeId, code)),
    [api, acceptSignIn],
  );

  const signInWithGoogle = useCallback(
    async (credential: string) => acceptSignIn(await api.googleSignIn(credential)),
    [api, acceptSignIn],
  );

  const selectOrg = useCallback(
    async (partnerId: string) => {
      const res = await api.selectOrg(partnerId);
      store.set(res);
      await hydrate();
    },
    [api, store, hydrate],
  );

  const createOrg = useCallback(
    async (name: string, type: PartnerType, terms: TermsAcceptance, afterScoped?: () => Promise<void>) => {
      const created = await api.createOrg(name, type, terms);
      // POST /org leaves the token unscoped (contract): trade it for a scoped one.
      const res = await api.selectOrg(created.id);
      store.set(res);
      if (afterScoped) await afterScoped();
      const list = [...memberships, { partner_id: created.id, display_name: created.display_name, role: 'owner' as const }];
      setMemberships(list);
      writeMemberships(list);
      await hydrate();
      return created.id;
    },
    [api, store, hydrate, memberships],
  );

  const refreshOrg = useCallback(async () => {
    setOrg(await api.getOrg());
  }, [api]);

  const reloadIdentity = useCallback(async () => {
    await client.refresh();
    const me = await api.whoami();
    setWhoami(me);
  }, [client, api]);

  const refreshWhoami = useCallback(async () => {
    setWhoami(await api.whoami());
  }, [api]);

  const markTermsAccepted = useCallback(() => {
    setWhoami((me) => (me ? { ...me, terms_required: false } : me));
  }, []);

  const signOut = useCallback(async () => {
    const refresh = store.getRefresh();
    if (refresh) await api.logout(refresh).catch(() => undefined);
    store.clear();
    writeMemberships([]);
    setMemberships([]);
    setWhoami(null);
    setOrg(null);
    setEndedReason(null);
    setStatus('signed_out');
  }, [api, store]);

  const value: SessionValue = {
    status,
    api,
    client,
    whoami,
    org,
    role: whoami?.role ?? null,
    memberships,
    endedReason,
    requestCode,
    verifyCode,
    signInWithGoogle,
    selectOrg,
    createOrg,
    refreshOrg,
    reloadIdentity,
    refreshWhoami,
    termsRequired: status === 'ready' && whoami?.terms_required === true,
    markTermsAccepted,
    signOut,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** For public pages that change when someone happens to be signed in (the docs). */
export function useOptionalSession(): SessionValue | null {
  return useContext(SessionContext);
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

function readMemberships(): MembershipSummary[] {
  try {
    const raw = window.localStorage.getItem(MEMBERSHIPS_KEY);
    return raw ? (JSON.parse(raw) as MembershipSummary[]) : [];
  } catch {
    return [];
  }
}
function writeMemberships(list: MembershipSummary[]) {
  try {
    window.localStorage.setItem(MEMBERSHIPS_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}
